import React, { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../../utils/api';

// StrategyHealthPanel — admin-only view of every live strategy's last-signal
// timestamp, age, today's count, last-24h count, mode, paused flag, status.
// Reads /strategies/api/admin/health/strategies/ every 20 seconds.

const REFRESH_MS = 20000;

const STATUS_STYLE = {
  HEALTHY: { bg: 'var(--tv-bull-tint)',  border: 'var(--tv-bull)',  fg: 'var(--tv-bull)'  },
  STALE:   { bg: 'var(--tv-gold-tint)',  border: 'var(--tv-gold)',  fg: 'var(--tv-gold)'  },
  DOWN:    { bg: 'var(--tv-bear-tint)',  border: 'var(--tv-bear)',  fg: 'var(--tv-bear)'  },
  PAUSED:  { bg: 'var(--tv-blue-tint)',  border: 'var(--tv-blue)',  fg: 'var(--tv-blue)'  },
};

function fmtAge(min) {
  if (min == null) return '—';
  if (min < 1)   return `${Math.round(min * 60)}s`;
  if (min < 60)  return `${min.toFixed(1)}m`;
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  if (h < 24)    return `${h}h ${m}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}

function fmtET(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
    const time = d.toLocaleTimeString('en-GB', {
      timeZone: 'America/New_York', hour12: false,
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
    return `${date} ${time}`;
  } catch { return iso; }
}

function StatusPill({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.DOWN;
  return (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: 4,
      fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
      textTransform: 'uppercase',
      background: s.bg, border: `1px solid ${s.border}`, color: s.fg,
    }}>{status}</span>
  );
}

export default function StrategyHealthPanel() {
  const [data, setData] = useState({ strategies: [], as_of: null, session: null, fresh_window_minutes: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await authFetch('/strategies/api/admin/health/strategies/');
      if (!res.ok) {
        setError(`HTTP ${res.status}`);
        return;
      }
      const j = await res.json();
      setData(j);
      setError('');
    } catch (e) {
      setError('Connection failed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  const items = data.strategies || [];
  const counts = items.reduce((acc, s) => { acc[s.status] = (acc[s.status] || 0) + 1; return acc; }, {});

  return (
    <div style={{ padding: '0' }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, marginBottom: 18, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--tv-text)' }}>Strategy Health</h2>
        <span style={{ fontSize: 11, color: 'var(--tv-text-muted)', fontFamily: 'Roboto Mono, monospace' }}>
          {data.as_of ? `as of ${fmtET(data.as_of)} ET` : '—'} · session {data.session || '—'} · stale &gt; {data.fresh_window_minutes ?? '—'}m
        </span>
        <button type="button" onClick={load} style={{
          marginLeft: 'auto', padding: '6px 14px', fontSize: 12, fontWeight: 600,
          border: '1px solid var(--tv-border)', borderRadius: 6, cursor: 'pointer',
          background: 'var(--tv-surface)', color: 'var(--tv-blue)',
        }}>Refresh</button>
      </div>

      {/* Status summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginBottom: 20 }}>
        {['HEALTHY', 'STALE', 'DOWN', 'PAUSED'].map(st => {
          const s = STATUS_STYLE[st];
          return (
            <div key={st} style={{
              padding: '14px 16px', borderRadius: 6,
              background: 'var(--tv-surface)',
              border: `1px solid var(--tv-border)`,
              borderLeft: `4px solid ${s.border}`,
            }}>
              <div style={{ fontSize: 10, color: 'var(--tv-text-muted)', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{st}</div>
              <div style={{ fontSize: 24, color: s.fg, fontFamily: 'Roboto Mono, monospace', marginTop: 4, fontWeight: 700 }}>{counts[st] || 0}</div>
            </div>
          );
        })}
      </div>

      {error && (
        <div style={{ padding: 12, marginBottom: 14, background: 'var(--tv-bear-tint)', border: '1px solid var(--tv-bear)', borderRadius: 6, color: 'var(--tv-bear)', fontSize: 12 }}>
          {error}
        </div>
      )}

      {/* Strategy table */}
      <div style={{ background: 'var(--tv-surface)', border: '1px solid var(--tv-border)', borderRadius: 8, overflowX: 'auto', overflowY: 'hidden', WebkitOverflowScrolling: 'touch' }}>
        <table style={{ width: '100%', minWidth: 760, borderCollapse: 'collapse', fontSize: 12 }}>
          <thead style={{ background: 'var(--tv-surface-hi)' }}>
            <tr>
              {['Strategy', 'Last Signal (ET)', 'Age', 'Today', '24h', 'Mode', 'Paused', 'Status'].map(h => (
                <th key={h} style={{
                  padding: '10px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700,
                  color: 'var(--tv-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em',
                  borderBottom: '1px solid var(--tv-border)',
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} style={{ padding: 20, textAlign: 'center', color: 'var(--tv-text-muted)' }}>Loading…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={8} style={{ padding: 20, textAlign: 'center', color: 'var(--tv-text-muted)' }}>No strategies returned</td></tr>
            )}
            {items.map(s => (
              <tr key={s.id} style={{ borderTop: '1px solid var(--tv-border-lt)' }}>
                <td style={{ padding: '10px 14px', fontWeight: 700, color: 'var(--tv-text)' }}>{s.id}</td>
                <td style={{ padding: '10px 14px', fontFamily: 'Roboto Mono, monospace', color: 'var(--tv-text-dim)' }}>{fmtET(s.last_signal_ts)}</td>
                <td style={{ padding: '10px 14px', fontFamily: 'Roboto Mono, monospace',
                              color: s.age_minutes != null && s.age_minutes > (data.fresh_window_minutes || 60) ? 'var(--tv-bear)' : 'var(--tv-text-dim)' }}>
                  {fmtAge(s.age_minutes)}
                </td>
                <td style={{ padding: '10px 14px', fontFamily: 'Roboto Mono, monospace', color: 'var(--tv-text)' }}>{s.today_count}</td>
                <td style={{ padding: '10px 14px', fontFamily: 'Roboto Mono, monospace', color: 'var(--tv-text)' }}>{s.last_24h_count}</td>
                <td style={{ padding: '10px 14px', fontFamily: 'Roboto Mono, monospace', color: 'var(--tv-text-dim)' }}>{s.mode || '—'}</td>
                <td style={{ padding: '10px 14px', color: s.paused ? 'var(--tv-blue)' : 'var(--tv-text-muted)', fontWeight: s.paused ? 700 : 400 }}>{s.paused ? 'YES' : 'no'}</td>
                <td style={{ padding: '10px 14px' }}><StatusPill status={s.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
